/* eslint-disable no-param-reassign */
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { ListRange } from "react-virtuoso";
import { TreeBrowserNode } from "../types/data-types";

export interface FlatNode<TFlatNode extends TreeBrowserNode> extends TreeBrowserNode {
    parentType?: NodeType;
    indentLevel?: number;
    childNodes: TFlatNode[];
}

const getFlatTreeRecursive = <TFlatNode extends FlatNode<TFlatNode>>(node: TFlatNode, nodesFlatTree: TFlatNode[], openNodes: string[], iterations = -1): TFlatNode[] => {
    let newNodeFlatTree = nodesFlatTree;
    iterations += 1;
    node.childNodes.forEach((nodeChild) => {
        newNodeFlatTree.push({ ...nodeChild, parentType: node.type as NodeType, indentLevel: iterations });
        if (openNodes.includes(nodeChild.id)) {
            newNodeFlatTree = getFlatTreeRecursive(nodeChild, newNodeFlatTree, openNodes, iterations);
        }
    });
    return newNodeFlatTree;
};

/** Flatterns a node tree in preorder traversal way */
export const getFlatTree = <TFlatNode extends FlatNode<TNode>, TNode extends TreeBrowserNode>(
    node: TNode,
    displayRootNode: boolean,
    parentNodeType: NodeType,
    openNodes: string[]
): TFlatNode[] => {
    let nodesFlatTree = [];
    if (displayRootNode) {
        nodesFlatTree.push({ ...node, parentType: parentNodeType });
    }
    if (!displayRootNode || openNodes.includes(node.id)) {
        nodesFlatTree = getFlatTreeRecursive(node, nodesFlatTree, openNodes);
    }
    return nodesFlatTree;
};

/** Finds which node needs to be fixed to top of virtual scroll container */
export function getFixedNode<TNode extends TreeBrowserNode>(fixedNodeIds: string[], flatNodeTree: FlatNode<TNode>[], indexesOfNodesInView: ListRange) {
    return flatNodeTree.find((node, index) => {
        // Is node in fixed node array
        if (!fixedNodeIds.includes(node.id)) {
            return false;
        }

        // Is node already in view
        if (isNodeBeingShown(index, indexesOfNodesInView)) {
            return false;
        }

        // Is one of node children in view
        if (flatNodeTree.some(({ parentId }, i) => parentId === node.sourceId && isNodeBeingShown(i, indexesOfNodesInView))) {
            return true;
        }

        return false;
    });
}

/** Is the node in virtual scroll container */
export const isNodeBeingShown = (index: number, indexRangeItemsInView: ListRange) => index >= indexRangeItemsInView?.startIndex && index <= indexRangeItemsInView?.endIndex;
